"use client";

import { tsv } from "@faire/slate/lib/tsv";
import {
  FloatingPortal,
  FloatingFocusManager,
  useMergeRefs,
  useTransitionStyles,
} from "@floating-ui/react";
import React from "react";

import { useFloatingContext } from "../floating/FloatingContext";

import {
  POPOVER_OPEN_TRANSITION_DURATION_MS,
  POPOVER_CLOSE_TRANSITION_DURATION_MS,
} from "./consts";
import { PopoverArrow } from "./PopoverArrow";
import { usePopoverContext } from "./PopoverContext";

type IProps = React.HTMLProps<HTMLDivElement> & {
  /**
   * Whether the popover content should trap focus.
   *
   * @default false
   */
  trapFocus?: boolean;
  /**
   * Whether to use a portal to render the popover.
   */
  portaled?: boolean;
  /**
   * Which element receives focus when the popover opens. Passed directly
   * to `FloatingFocusManager`. A numeric index selects the nth tabbable
   * element; a ref points to a specific element. Only meaningful when
   * `trapFocus` is true.
   */
  initialFocus?: number | React.MutableRefObject<HTMLElement | null>;
};

export const PopoverContent: React.FC<IProps> = ({
  portaled,
  ...props
}) => {
  const { floatingContext } = usePopoverContext();

  // useTransitionStyles does not accept floating root context
  // we have to use the context created from `useFloating` instead
  const { isMounted } = useTransitionStyles(floatingContext);

  if (!isMounted) {
    return null;
  }

  if (portaled) {
    return (
      <FloatingPortal>
        <PopoverBase {...props} />
      </FloatingPortal>
    );
  }

  return <PopoverBase {...props} />;
};

const PopoverBase: React.FC<Omit<IProps, "portaled">> = ({
  children,
  className,
  ref: propRef,
  style,
  trapFocus,
  initialFocus,
  ...props
}) => {
  const { refs, floatingStyles, propGetters } = useFloatingContext();
  const { floatingContext } = usePopoverContext();
  const ref = useMergeRefs([refs.setFloating, propRef]);
  const { styles: transitionStyles } = useTransitionStyles(floatingContext, {
    duration: {
      open: POPOVER_OPEN_TRANSITION_DURATION_MS,
      close: POPOVER_CLOSE_TRANSITION_DURATION_MS,
    },
  });

  const popoverCls = popoverStyle.cls({ className });

  return (
    <FloatingFocusManager
      context={floatingContext}
      disabled={!trapFocus}
      modal={trapFocus}
      initialFocus={initialFocus}
    >
      <div
        {...props}
        className={popoverCls}
        ref={ref}
        style={{
          ...floatingStyles,
          ...transitionStyles,
          ...style,
        }}
        {...propGetters.getFloatingProps(props)}
      >
        {children}
        <PopoverArrow />
      </div>
    </FloatingFocusManager>
  );
};

const popoverStyle = tsv({
  vars: {
    zIndex: "fs-popover-z-index",
  },
  base: "fs-popover z-(--fs-popover-z-index)",
});

export const popoverVars = popoverStyle.vars;
